import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link, useOutletContext } from 'react-router-dom';
import { pwaUrl, boPath } from '@/utils/getBasePath';
import { addAccess, setAccess, detailText, formatPhone } from '@/lib/posLoginAccess';
import {
  Users, Plus, Search, Edit, Trash2, MoreHorizontal, Mail,
  Phone, Shield, Clock, Calendar, UserCheck, UserX, Key,
  Link as LinkIcon, Copy, QrCode, ExternalLink, RefreshCw,
  Lock, Unlock, KeyRound, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { useIsMobile } from '@/hooks/useIsMobile';
import { copyText } from '@/lib/copyText';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Role configuration
// The staff roles of the ONE permission catalog (backend/security/permission_catalog.py).
// "owner" is never a staff role; the server refuses any other value.
const ROLES = {
  manager: { label: 'Manager', color: 'bg-blue-100 text-blue-800', permissions: 'Everything on the floor, approvals, refunds' },
  host: { label: 'Host', color: 'bg-pink-100 text-pink-800', permissions: 'Seat, move and merge tables, reservations' },
  waiter: { label: 'Waiter', color: 'bg-cyan-100 text-cyan-800', permissions: 'Tables, orders, send to kitchen, take payments' },
  kitchen: { label: 'Kitchen', color: 'bg-orange-100 text-orange-800', permissions: 'Kitchen display' },
  expo: { label: 'Expo', color: 'bg-amber-100 text-amber-800', permissions: 'Ready food, served' },
  cashier: { label: 'Cashier', color: 'bg-green-100 text-green-800', permissions: 'Checks and payments' },
};
// shown, never assignable: the owner's own POS profile (POS Login Access, linked to the owner login)
const SHOWN_ROLES = { ...ROLES, owner: { label: 'Owner', color: 'bg-purple-100 text-purple-800' } };

export default function EmployeesPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null } = context;
  const isMobile = useIsMobile();
  
  // Employee state
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  // Restaurant info for PWA links
  const [restaurant, setRestaurant] = useState(null);
  
  // PIN Management state
  const [pinPolicy, setPinPolicy] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [tempPin, setTempPin] = useState(null);
  const [pinLoading, setPinLoading] = useState(false);
  
  // Form state
  const [employeeForm, setEmployeeForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: 'cashier',
    pin: '',
    is_active: true,
    allow_pos_login: false
  });

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchEmployees = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/merchant/staff/pins`, getHeaders());
      setEmployees(response.data.staff || []);
      setPinPolicy(response.data.pin_policy || 'PIN must be 4-8 digits');
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load staff list');
    } finally {
      setLoading(false);
    }
  });

  const fetchRestaurant = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/merchant/restaurant/me`, getHeaders());
      setRestaurant(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
    }
  });

  useEffect(() => {
    if (token) {
      fetchEmployees();
      fetchRestaurant();
    }
  }, [token, fetchEmployees, fetchRestaurant]);

  // PIN Management Functions
  const handleSetPin = async () => {
    if (!selectedStaff || !pinInput) return;
    
    setPinLoading(true);
    try {
      await axios.post(
        `${API}/api/merchant/staff/pins/${selectedStaff.id}/set`,
        { staff_id: selectedStaff.id, pin: pinInput },
        getHeaders()
      );
      toast.success(`PIN set for ${selectedStaff.name}`);
      setShowPinModal(false);
      setPinInput('');
      setSelectedStaff(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleResetPin = async (mode) => {
    if (!selectedStaff) return;
    
    setPinLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/merchant/staff/pins/${selectedStaff.id}/reset`,
        { mode },
        getHeaders()
      );
      
      if (mode === 'generate' && response.data.temp_pin) {
        setTempPin(response.data.temp_pin);
        toast.success('Temporary PIN generated');
      } else {
        toast.success(`PIN cleared for ${selectedStaff.name}`);
        setShowResetModal(false);
        setSelectedStaff(null);
      }
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset PIN');
    } finally {
      setPinLoading(false);
    }
  };

  const handleUnlock = async (staff) => {
    try {
      await axios.post(
        `${API}/api/merchant/staff/pins/${staff.id}/unlock`,
        {},
        getHeaders()
      );
      toast.success(`PIN unlocked for ${staff.name}`);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to unlock PIN');
    }
  };

  const copyToClipboard = (text, label) => {
    copyText(text, `${label} copied to clipboard`);
  };

  const handleSaveEmployee = async () => {
    try {
      if (editingEmployee) {
        await axios.put(
          `${API}/api/restaurant-mgmt/staff/${editingEmployee.id}`,
          employeeForm,
          getHeaders()
        );
        toast.success('Employee updated');
      } else {
        await axios.post(
          `${API}/api/restaurant-mgmt/staff`,
          // no default password: the employee signs in with the POS PIN set below
          { ...employeeForm, restaurant_id: manager?.restaurant_id, invite: true },
          getHeaders()
        );
        toast.success('Employee added');
      }
      setShowEmployeeDialog(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(detailText(error, 'Failed to save employee'));
    }
  };

  // POS Login Access: the same doors as Staff & Labor -> POS Login Access
  const togglePosLogin = async (emp) => {
    try {
      if (emp.pos_login === 'allowed') {
        const out = await setAccess(token, emp.id, false);
        toast.success(`${emp.name} can no longer sign in to the POS (${out.devices_revoked} device${out.devices_revoked === 1 ? '' : 's'} signed out)`);
      } else {
        if (!emp.phone) { toast.error(`Add a phone for ${emp.name} first`); return; }
        const out = await addAccess(token, [{ phone: emp.phone, role: emp.role }]);
        const r = out.results[0];
        if (!r.ok) { toast.error(detailText(r.error, 'Could not allow this phone')); return; }
        toast.success(`${emp.name} may sign in to the POS with ${formatPhone(r.entry.phone)}`);
      }
      fetchEmployees();
    } catch (error) {
      toast.error(detailText(error, 'Could not change POS login'));
    }
  };

  const posLoginBadge = (emp) => (emp.pos_login === 'allowed'
    ? <Badge className="bg-blue-100 text-blue-800 gap-1" data-testid={`pos-login-${emp.id}`}><Shield className="w-3 h-3" />POS login</Badge>
    : <Badge variant="outline" className="text-slate-600 gap-1" data-testid={`pos-login-${emp.id}`}>No POS login</Badge>);

  const handleDeleteEmployee = async (employee) => {
    if (!confirm(`Remove ${employee.name || employee.full_name} from staff?`)) return;
    
    try {
      await axios.delete(`${API}/api/restaurant-mgmt/staff/${employee.id}`, getHeaders());
      toast.success('Employee removed');
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to remove employee');
    }
  };

  const resetForm = () => {
    setEditingEmployee(null);
    setEmployeeForm({
      full_name: '',
      email: '',
      phone: '',
      role: 'cashier',
      pin: '',
      is_active: true,
      allow_pos_login: false
    });
  };

  const openEditDialog = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      full_name: employee.name || employee.full_name,
      email: employee.email,
      phone: employee.phone || '',
      role: employee.role,
      pin: '',
      is_active: employee.is_active,
      allow_pos_login: employee.pos_login === 'allowed'
    });
    setShowEmployeeDialog(true);
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const name = emp.name || emp.full_name || '';
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (emp.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || emp.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Stats
  const stats = {
    total: employees.length,
    active: employees.filter(e => e.is_active).length,
    withPin: employees.filter(e => e.has_pin).length,
    locked: employees.filter(e => e.is_locked).length
  };

  // PWA Link generation
  // The apps sign in to the restaurant on the device, so the link is the app itself
  // (pos.chain29.com ...), not this back office's origin + a slug.
  const getPWALinks = () => ({
    pos: pwaUrl('pos'),
    kiosk: pwaUrl('kiosk'),
    timeclock: pwaUrl('timeclock', '/pwa/timeclock'),
  });

  const pwaLinks = getPWALinks();

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="employees-page">
      {/* Header - Responsive */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900 md:text-2xl">Staff & PIN Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage team members, PINs, and access links. Only phones on{' '}
            <Link to={boPath('pos-login-access')} className="text-blue-700 underline" data-testid="pos-login-access-link">POS Login Access</Link>
            {' '}can sign in to the POS.
          </p>
        </div>
        <Button 
          className="bg-gradient-to-r from-blue-600 to-purple-600 w-full sm:w-auto min-h-[44px]"
          onClick={() => { resetForm(); setShowEmployeeDialog(true); }}
          data-testid="add-employee-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Employee
        </Button>
      </div>

      {/* PWA Quick Links Card - Responsive */}
      {restaurant?.slug && (
        <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardHeader className="pb-2 px-3 sm:px-6 pt-3 sm:pt-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <LinkIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Quick Access Links
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">Share these links with your staff for quick access</CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* POS Link */}
              <div className="bg-white rounded-lg p-3 sm:p-4 border shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700 text-sm">POS Terminal</span>
                  <Badge variant="outline" className="text-xs">Staff</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    value={pwaLinks?.pos || ''} 
                    readOnly 
                    className="text-xs h-8 bg-slate-50 flex-1 min-w-0"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(pwaLinks?.pos, 'POS link')}
                    data-testid="copy-pos-link"
                    className="flex-shrink-0 min-w-[36px] min-h-[36px]"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Kiosk Link */}
              <div className="bg-white rounded-lg p-3 sm:p-4 border shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700 text-sm">Self-Order Kiosk</span>
                  <Badge variant="outline" className="text-xs">Customer</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    value={pwaLinks?.kiosk || ''} 
                    readOnly 
                    className="text-xs h-8 bg-slate-50 flex-1 min-w-0"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(pwaLinks?.kiosk, 'Kiosk link')}
                    data-testid="copy-kiosk-link"
                    className="flex-shrink-0 min-w-[36px] min-h-[36px]"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Timeclock Link */}
              <div className="bg-white rounded-lg p-3 sm:p-4 border shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700 text-sm">Time Clock</span>
                  <Badge variant="outline" className="text-xs">Staff</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Input 
                    value={pwaLinks?.timeclock || ''} 
                    readOnly 
                    className="text-xs h-8 bg-slate-50 flex-1 min-w-0"
                  />
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => copyToClipboard(pwaLinks?.timeclock, 'Timeclock link')}
                    className="flex-shrink-0 min-w-[36px] min-h-[36px]"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards - Responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Total Staff</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Active</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">With PIN</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.withPin}</p>
              </div>
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Key className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {stats.locked > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-red-600">Locked</p>
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.locked}</p>
                </div>
                <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Filters - Responsive */}
      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-48 h-10">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {Object.entries(ROLES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={fetchEmployees} className="min-w-[44px] min-h-[44px]">
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Refresh</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee List - Mobile Cards / Desktop Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No employees found
            </div>
          ) : isMobile ? (
            // Mobile Card View
            <div className="divide-y divide-slate-100">
              {filteredEmployees.map((emp) => {
                const roleConfig = SHOWN_ROLES[emp.role] || { label: emp.role, color: 'bg-slate-100 text-slate-800' };
                return (
                  <div key={emp.id} className="p-4" data-testid={`employee-card-${emp.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-10 w-10 flex-shrink-0">
                          <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white text-sm">
                            {(emp.name || emp.full_name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{emp.name || emp.full_name}</p>
                          <p className="text-xs text-slate-500 truncate">{emp.email}</p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="min-w-[44px] min-h-[44px]" data-testid={`employee-actions-${emp.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(emp)} className="min-h-[44px]">
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => {
                            setSelectedStaff(emp);
                            setPinInput('');
                            setShowPinModal(true);
                          }} className="min-h-[44px]">
                            <Key className="w-4 h-4 mr-2" />
                            Set PIN
                          </DropdownMenuItem>
                          {emp.has_pin && (
                            <DropdownMenuItem onClick={() => {
                              setSelectedStaff(emp);
                              setTempPin(null);
                              setShowResetModal(true);
                            }} className="min-h-[44px]">
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Reset PIN
                            </DropdownMenuItem>
                          )}
                          {emp.is_locked && (
                            <DropdownMenuItem onClick={() => handleUnlock(emp)} className="min-h-[44px]">
                              <Unlock className="w-4 h-4 mr-2" />
                              Unlock PIN
                            </DropdownMenuItem>
                          )}
                          {!emp.linked_login && emp.is_active && (
                            <DropdownMenuItem onClick={() => togglePosLogin(emp)} className="min-h-[44px]">
                              <Shield className="w-4 h-4 mr-2" />
                              {emp.pos_login === 'allowed' ? 'Turn off POS login' : 'Allow POS login'}
                            </DropdownMenuItem>
                          )}
                          {!emp.linked_login && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600 min-h-[44px]"
                                onClick={() => handleDeleteEmployee(emp)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                      <Badge className={roleConfig.color + " text-xs"}>{roleConfig.label}</Badge>
                      {posLoginBadge(emp)}
                      {emp.is_locked ? (
                        <Badge variant="destructive" className="gap-1 text-xs">
                          <Lock className="w-3 h-3" />
                          Locked
                        </Badge>
                      ) : emp.has_pin ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 gap-1 text-xs">
                          <Key className="w-3 h-3" />
                          PIN Set
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400 gap-1 text-xs">
                          <KeyRound className="w-3 h-3" />
                          No PIN
                        </Badge>
                      )}
                      {emp.is_active ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-400 text-xs">Inactive</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Desktop Table View
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-center">PIN Status</TableHead>
                  <TableHead className="text-center">POS Login</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((emp) => {
                  const roleConfig = SHOWN_ROLES[emp.role] || { label: emp.role, color: 'bg-slate-100 text-slate-800' };
                  return (
                    <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white text-sm">
                              {(emp.name || emp.full_name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{emp.name || emp.full_name}</p>
                            <p className="text-xs text-slate-500">{emp.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleConfig.color}>{roleConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {emp.phone && (
                          <span className="text-sm text-slate-600">{emp.phone}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.is_locked ? (
                          <Badge variant="destructive" className="gap-1">
                            <Lock className="w-3 h-3" />
                            Locked
                          </Badge>
                        ) : emp.has_pin ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 gap-1">
                            <Key className="w-3 h-3" />
                            Set
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400 gap-1">
                            <KeyRound className="w-3 h-3" />
                            Not Set
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex flex-col items-center gap-1">
                          {posLoginBadge(emp)}
                          {!emp.linked_login && emp.is_active && (
                            <button type="button" className="text-xs text-blue-700 underline min-h-[24px]"
                              onClick={() => togglePosLogin(emp)} data-testid={`pos-login-toggle-${emp.id}`}>
                              {emp.pos_login === 'allowed' ? 'Turn off' : 'Allow POS login'}
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {emp.is_active ? (
                          <Badge className="bg-green-100 text-green-700">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-400">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`employee-actions-${emp.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(emp)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => {
                              setSelectedStaff(emp);
                              setPinInput('');
                              setShowPinModal(true);
                            }}>
                              <Key className="w-4 h-4 mr-2" />
                              Set PIN
                            </DropdownMenuItem>
                            {emp.has_pin && (
                              <DropdownMenuItem onClick={() => {
                                setSelectedStaff(emp);
                                setTempPin(null);
                                setShowResetModal(true);
                              }}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Reset PIN
                              </DropdownMenuItem>
                            )}
                            {emp.is_locked && (
                              <DropdownMenuItem onClick={() => handleUnlock(emp)}>
                                <Unlock className="w-4 h-4 mr-2" />
                                Unlock PIN
                              </DropdownMenuItem>
                            )}
                            {!emp.linked_login && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => handleDeleteEmployee(emp)}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Set PIN Modal */}
      <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set PIN for {selectedStaff?.name}</DialogTitle>
            <DialogDescription>{pinPolicy}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Enter PIN</Label>
              <div className="relative">
                <Input
                  type={showPin ? "text" : "password"}
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter 4-8 digit PIN"
                  className="pr-10 text-center text-xl tracking-widest"
                  maxLength={8}
                  data-testid="pin-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowPin(!showPin)}
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinModal(false)}>Cancel</Button>
            <Button 
              onClick={handleSetPin}
              disabled={pinInput.length < 4 || pinLoading}
              data-testid="confirm-set-pin"
            >
              {pinLoading ? 'Setting...' : 'Set PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Modal */}
      <Dialog open={showResetModal} onOpenChange={(open) => {
        setShowResetModal(open);
        if (!open) setTempPin(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset PIN for {selectedStaff?.name}</DialogTitle>
            <DialogDescription>
              Choose how to reset the PIN
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {tempPin ? (
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription>
                  <div className="text-center">
                    <p className="text-sm text-slate-600 mb-2">Temporary PIN (share with staff member):</p>
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-3xl font-mono font-bold tracking-widest text-green-700">
                        {tempPin}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(tempPin, 'Temporary PIN')}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      This PIN is shown only once. Make sure to share it securely.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid gap-3">
                <Button
                  variant="outline"
                  className="h-auto py-4 justify-start"
                  onClick={() => handleResetPin('generate')}
                  disabled={pinLoading}
                >
                  <RefreshCw className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Generate Temporary PIN</p>
                    <p className="text-sm text-slate-500">Create a random PIN to share with the staff member</p>
                  </div>
                </Button>
                <Button
                  variant="outline"
                  className="h-auto py-4 justify-start text-red-600 hover:text-red-700"
                  onClick={() => handleResetPin('clear')}
                  disabled={pinLoading}
                >
                  <Trash2 className="w-5 h-5 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Clear PIN</p>
                    <p className="text-sm text-slate-500">Remove the PIN completely</p>
                  </div>
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowResetModal(false);
              setTempPin(null);
            }}>
              {tempPin ? 'Done' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Employee Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={employeeForm.full_name}
                onChange={(e) => setEmployeeForm({...employeeForm, full_name: e.target.value})}
                placeholder="Enter full name"
                data-testid="employee-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                placeholder="Enter email"
                data-testid="employee-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm({...employeeForm, phone: e.target.value})}
                readOnly={!!editingEmployee?.linked_login}
                data-testid="employee-phone-input"
                placeholder="Enter phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              {editingEmployee?.linked_login ? (
                <p className="text-sm text-slate-700" data-testid="role-locked">
                  {SHOWN_ROLES[employeeForm.role]?.label || employeeForm.role} - this is the {employeeForm.role === 'owner' ? "owner's" : "manager's"} own
                  POS profile (linked to their login); it changes only on POS Login Access.
                </p>
              ) : (
              <Select 
                value={employeeForm.role} 
                onValueChange={(v) => setEmployeeForm({...employeeForm, role: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
            </div>
            {!editingEmployee?.linked_login && (
              <div className="flex items-start gap-3">
                <Switch
                  checked={employeeForm.allow_pos_login}
                  onCheckedChange={(v) => setEmployeeForm({...employeeForm, allow_pos_login: v})}
                  data-testid="allow-pos-login-switch"
                />
                <div>
                  <Label>Allow POS login</Label>
                  <p className="text-xs text-slate-600">This phone may sign in to the POS (texted code, then their PIN).</p>
                </div>
              </div>
            )}
            {!editingEmployee?.linked_login && (
            <div className="flex items-center gap-3">
              <Switch
                checked={employeeForm.is_active}
                onCheckedChange={(v) => setEmployeeForm({...employeeForm, is_active: v})}
              />
              <Label>Active</Label>
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEmployee} data-testid="employee-save-btn">
              {editingEmployee ? 'Update' : 'Add'} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
